// @ts-strict-ignore
import {effect, inject, Injectable, signal} from "@angular/core";
import {FormArray, FormBuilder, FormControl, FormGroup, FormRecord, Validators} from "@angular/forms";
import {
	Assessment,
	ExamAssessmentWrapper,
	ExamFlow,
	SessionPhaseType,
	ExamSession,
	Participant,
	ParticipantType,
	Scenario,
	SimUser,
	TrainingRoom,
} from "@models";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {SimExamFlowService, SimSessionService} from "@services";
import {distinctUntilChanged, Subject} from "rxjs";
import {DatePipe} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

export type ExamSession_Step1Form = FormGroup<{
	title: FormControl<string>;
	examFlow: FormControl<ExamFlow>;
	date: FormControl<Date>;
	time: FormControl<string>;
	confirmationDurationMinutes: FormControl<number>;
	closingDurationMinutes: FormControl<number>;
	floorScreenRooms: FormControl<TrainingRoom[]>;
}>;
export type ExamSession_Step2Form = FormArray<ExamSession_StationForm>;
export type ExamSession_Step3Form = FormControl<SimUser[]>;
export type ExamSession_StationForm = FormGroup<{
	trainingRoom: FormControl<TrainingRoom>;
	scenario: FormControl<Scenario>;
	examiner: FormControl<SimUser>;
	standardizedPatient: FormControl<SimUser>;
	assessments: FormRecord<ExamSession_AssessmentForm>;
}>;
export type ExamSession_AssessmentForm = FormGroup<{
	examiner: FormControl<Assessment>;
	student: FormControl<Assessment>;
	standardizedPatient: FormControl<Assessment>;
}>;

@UntilDestroy()
@Injectable()
export class ExamSessionStateService {
	// Signals
	public isLoading = signal(true);
	public excludeUserIds = signal<string[]>([]);
	public excludeExaminerIds = signal<string[]>([]);
	public excludeRoomIds = signal<string[]>([]);
	private _examPhases = signal<SessionPhaseType[]>([]);
	public examPhases = this._examPhases.asReadonly();

	// Forms
	public step1Form: ExamSession_Step1Form;
	public step2Form: ExamSession_Step2Form;
	public step3Form: ExamSession_Step3Form;

	// Properties
	public session: ExamSession;
	public savePromise: Promise<ExamSession | Participant[]>;
	public $warningCheck = new Subject<void>();
	private _formBuilder = new FormBuilder();
	private _isExaminerMandatory: boolean;
	private _isPatientMandatory: boolean;

	// Injections
	private _examFlowService = inject(SimExamFlowService);
	private _sessionService = inject(SimSessionService);
	private _datePipe = inject(DatePipe);
	private _translate = inject(TranslateService);
	private T = Translations;

	constructor() {
		effect(() => this._effectBuildAssessmentForm());
	}

	public get emptyStation(): ExamSession_StationForm {
		const result = this._formBuilder.group({
			trainingRoom: new FormControl<TrainingRoom>(null, [Validators.required]),
			scenario: new FormControl<Scenario>(null, [Validators.required]),
			examiner: new FormControl<SimUser>(null, this._isExaminerMandatory ? [Validators.required] : []),
			standardizedPatient: new FormControl<SimUser>(null, this._isPatientMandatory ? [Validators.required] : []),
			assessments: new FormRecord<ExamSession_AssessmentForm>({}),
		});
		this._addAssessmentsToStationForm(result);
		return result;
	}

	public updateExamFlowDependencies(examFlow: ExamFlow): void {
		// Phases
		this._examPhases.set(
			examFlow.examPhases
				.filter((phase) => phase.type !== SessionPhaseType.StudentReady)
				.filter((phase) => phase.isActive)
				.map((phase) => phase.type),
		);

		// Mandatory Participants
		const isExaminerMandatory = examFlow.examFlowParticipantDevices.some(
			(setting) => setting.participantType === ParticipantType.Instructor && setting.isPresent,
		);
		const isPatientMandatory = examFlow.examFlowParticipantDevices.some(
			(setting) => setting.participantType === ParticipantType.StandardizedPatient && setting.isPresent,
		);
		if (this._isExaminerMandatory !== isExaminerMandatory || this._isPatientMandatory !== isPatientMandatory) {
			this._isExaminerMandatory = isExaminerMandatory;
			this._isPatientMandatory = isPatientMandatory;
			this.step2Form?.controls?.forEach((stationForm) => {
				// Examiner
				stationForm.controls.examiner.setValidators(isExaminerMandatory ? [Validators.required] : []);
				stationForm.controls.examiner.markAsDirty();
				stationForm.controls.examiner.updateValueAndValidity();
				// Standardized Patient
				stationForm.controls.standardizedPatient.setValidators(isPatientMandatory ? [Validators.required] : []);
				stationForm.controls.standardizedPatient.markAsDirty();
				stationForm.controls.standardizedPatient.updateValueAndValidity();
			});
		}
	}

	public async initState(sessionId: string | undefined, eventId: string, eventDay: Date | string): Promise<void> {
		this.session = (
			!!sessionId
				? await this._sessionService.getSessionById(sessionId)
				: {
						id: "",
						eventId,
						examStations: [],
						students: [],
						participants: [],
					}
		) as ExamSession;
		this._initForm(eventDay);
		this._trackExcludeIds();
		await this._populateForm();
		this.isLoading.set(false);
	}

	private _initForm(eventDay: Date | string) {
		// Step 1
		this.step1Form = this._formBuilder.group({
			title: new FormControl<string>(null, [Validators.required]),
			examFlow: new FormControl<ExamFlow>(null, [Validators.required]),
			date: new FormControl(new Date(eventDay), [Validators.required]),
			time: new FormControl(null, [Validators.required]),
			confirmationDurationMinutes: new FormControl(null, [Validators.required]),
			closingDurationMinutes: new FormControl(null, [Validators.required]),
			floorScreenRooms: new FormControl<TrainingRoom[]>([]),
		});

		// Step 2
		this.step2Form = new FormArray<ExamSession_StationForm>([]);
		// Step 2: Edit
		if (this.session.examStations.length) {
			this.step2Form = this._formBuilder.array(
				this.session.examStations.map((station) => {
					return this._formBuilder.group({
						trainingRoom: new FormControl<TrainingRoom>(
							station.trainingRoomId ? ({id: station.trainingRoomId} as TrainingRoom) : null,
							[Validators.required],
						),
						scenario: new FormControl<Scenario>(station.scenarioId ? ({id: station.scenarioId} as Scenario) : null, [
							Validators.required,
						]),
						examiner: new FormControl<SimUser>(
							station.examinerUserId ? ({id: station.examinerUserId} as SimUser) : null,
							this._isExaminerMandatory ? [Validators.required] : [],
						),
						standardizedPatient: new FormControl<SimUser>(
							station.standardizedPatientUserId ? ({id: station.standardizedPatientUserId} as SimUser) : null,
							this._isPatientMandatory ? [Validators.required] : [],
						),
						assessments: new FormRecord<ExamSession_AssessmentForm>({}),
					});
				}),
			);
		}

		// Step 3
		this.step3Form = new FormControl<SimUser[]>(this.session.students?.map((student) => student.user) ?? [], [Validators.required]);
	}

	private _collectUserIds(): string[] {
		const step2UserIds = this.step2Form.value
			.flatMap((stationForm) => [stationForm.examiner?.id, stationForm.standardizedPatient?.id])
			.filter((id) => id !== null);

		const step3UserIds = this.step3Form.value.map((student) => student.id);

		return [...step2UserIds, ...step3UserIds];
	}

	private _collectExaminerIds(): string[] {
		const step2Ids = this._isExaminerMandatory
			? this.step2Form.value.flatMap((stationForm) => [stationForm.examiner?.id, stationForm.standardizedPatient?.id])
			: this.step2Form.value.flatMap((stationForm) => [stationForm.standardizedPatient?.id]);

		const step3Ids = this.step3Form.value.map((student) => student.id);

		return [...step2Ids, ...step3Ids].filter((id) => id !== null);
	}

	private _trackExcludeIds(): void {
		const updateExcludeRoomIds = () => {
			const step1Ids = this.step1Form.value.floorScreenRooms.map((room) => room.id);
			const step2Ids = this.step2Form.value.map((stationForm) => stationForm.trainingRoom?.id);
			this.excludeRoomIds.set([...step1Ids, ...step2Ids]);
		};

		// Step 1
		this.step1Form.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((_) => updateExcludeRoomIds());

		// Step 2
		this.step2Form.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((formValue) => {
			this.$warningCheck.next();
			updateExcludeRoomIds();
			this.excludeUserIds.set(this._collectUserIds());
			this.excludeExaminerIds.set(this._collectExaminerIds());
		});

		// Step 3
		this.step3Form.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((formValue) => {
			this.$warningCheck.next();
			this.excludeUserIds.set(this._collectUserIds());
			this.excludeExaminerIds.set(this._collectExaminerIds());
		});
	}

	private _effectBuildAssessmentForm(): void {
		if (this.examPhases().length && this.step2Form) {
			this.step2Form.controls.forEach((stationForm, index) => {
				const prefillData = this.session.examStations?.at(index).assessmentWrappers;
				this._addAssessmentsToStationForm(stationForm as ExamSession_StationForm, prefillData);
			});
		}
	}

	private _addAssessmentsToStationForm(stationForm: ExamSession_StationForm, prefillData: ExamAssessmentWrapper[] = []): void {
		for (const phase of this.examPhases()) {
			// Skip if already added
			if (stationForm.controls.assessments.contains(SessionPhaseType[phase])) {
				continue;
			}

			// Get prefilled assessments
			const examinerAssessmentId = getPrefillAssessmentId(phase, ParticipantType.Instructor);
			const studentAssessmentId = getPrefillAssessmentId(phase, ParticipantType.Learner);
			const spAssessmentId = getPrefillAssessmentId(phase, ParticipantType.StandardizedPatient);

			stationForm.controls.assessments.addControl(
				SessionPhaseType[phase],
				this._formBuilder.group({
					examiner: new FormControl<Assessment>(examinerAssessmentId ? ({id: examinerAssessmentId} as Assessment) : null),
					student: new FormControl<Assessment>(studentAssessmentId ? ({id: studentAssessmentId} as Assessment) : null),
					standardizedPatient: new FormControl<Assessment>(spAssessmentId ? ({id: spAssessmentId} as Assessment) : null),
				}),
			);
		}

		function getPrefillAssessmentId(phase: SessionPhaseType, participantType: ParticipantType): string | null {
			return prefillData.find((assessment) => assessment.sessionPhaseType === phase && assessment.participantType === participantType)
				?.assessmentId;
		}
	}

	private async _populateForm() {
		// Step1
		if (this.session.id) {
			this.step1Form.patchValue(
				{
					title: this.session.title,
					examFlow: await this._examFlowService.getExamFlowById(this.session.examFlowId),
					date: new Date(this.session.startTime),
					time: this._datePipe.transform(new Date(this.session.startTime), this._translate.instant(this.T.DATE_FORMAT_TIME)),
					confirmationDurationMinutes: this.session.confirmation.durationInSeconds / 60,
					closingDurationMinutes: this.session.closing.durationInSeconds / 60,
					floorScreenRooms: this.session.floorRooms,
				},
				{emitEvent: false},
			);
			this.updateExamFlowDependencies(this.step1Form.value.examFlow);
		}

		// Step 2
		if (this.session.examStations.length) {
			this.step2Form.setValue(
				this.session.examStations.map((station) => {
					return {
						trainingRoom: station.trainingRoomId ? ({id: station.trainingRoomId} as TrainingRoom) : null,
						scenario: station.scenarioId ? ({id: station.scenarioId} as Scenario) : null,
						examiner: station.examinerUserId ? ({id: station.examinerUserId} as SimUser) : null,
						standardizedPatient: station.standardizedPatientUserId
							? ({id: station.standardizedPatientUserId} as SimUser)
							: null,
						assessments: {},
					};
				}),
			);
		}

		// Step 3
		if (this.session.students?.length) {
			this.step3Form.setValue(this.session.students?.map((student) => student.user));
		}
	}
}
